import React, { useState, useCallback, useEffect, useRef } from "react";
import axios from "axios";
import styled, { keyframes, css } from "styled-components";
import { useCheckSessionHeartBeat } from "components/API/ApiEnpoints";
import {
  FaRegCalendarAlt,
  FaUsers,
  FaUserCheck,
  FaTrophy,
  FaRunning,
  FaHeartbeat,
  FaBullseye,
  FaCheckCircle,
} from "react-icons/fa";
import { IoFootsteps } from "react-icons/io5";
import { RiPinDistanceFill, RiFireLine } from "react-icons/ri";
import { MdOutlineAutoGraph, MdLeaderboard } from "react-icons/md";
import { HiMiniUserPlus } from "react-icons/hi2";
import openNotification from "components/utils/CustomNotification";

// ─── ENVIRONMENT & CONSTANTS ──────────────────────────────────────────────────
// Added (window as any) to prevent TypeScript errors if _env_ isn't globally declared
const beAddress = (window as any)._env_?.REACT_APP_API_URL || process.env.REACT_APP_API_URL;

// ─── DATE HELPERS ─────────────────────────────────────────────────────────────
const formatDate = (date: Date) => date.toISOString().split("T")[0];

const toApiDateTime = (dateValue: string, timeValue: string) =>
  `${dateValue}T${timeValue}`;

const getDefaultRange = () => {
  const today = new Date();
  const fromDate = new Date();
  fromDate.setDate(today.getDate() - 6);

  return {
    fromDate: formatDate(fromDate),
    toDate: formatDate(today),
  };
};

// ─── DESIGN TOKENS ────────────────────────────────────────────────────────────
const C = {
  teal:          "var(--svg-color, #2BBFAA)",
  tealDark:      "var(--menu-item-color, #1A9080)",
  tealLight:     "var(--sidebar-hover-color, #E0F7F4)",
  navy:          "var(--text-color, #0D2237)",
  bg:            "var(--page-color, #EAEEF0)",
  surface:       "var(--card-background, #FFFFFF)",
  surfaceAlt:    "var(--card-background, #F7F9FA)",
  text:          "var(--text-color, #0D2237)",
  textMid:       "var(--text-color, #3D5A6A)",
  muted:         "var(--menu-item-color, #7A9BAA)",
  border:        "var(--line-color, #DDE4E8)",
  positive:      "var(--svg-color, #2BBFAA)",
  positiveLight: "rgba(46, 214, 189, 0.18)",
  negative:      "#E05C5C",
  negativeLight: "rgba(224,92,92,0.10)",
  neutral:       "var(--menu-item-color, #8BAAB8)",
  neutralLight:  "rgba(139,170,184,0.12)",
  distColor:     "#3B82F6",
  distLight:     "rgba(59,130,246,0.12)",
  calColor:      "#F59E0B",
  calLight:      "rgba(245,158,11,0.12)",
  shadow:        "0 1px 4px rgba(13,34,55,0.07), 0 4px 16px rgba(13,34,55,0.06)",
  shadowMd:      "0 2px 8px rgba(13,34,55,0.08), 0 8px 24px rgba(13,34,55,0.08)",
} as const;

// ─── API TYPES ────────────────────────────────────────────────────────────────
interface DateRange {
  from_date: string;
  to_date: string;
}

interface UsersInfoMap {
  total_users_count: number;
  total_registered_users_count: number;
  registered_users_within_duration_count: number;
  registered_users_within_duration: unknown[];
}

interface CounterWithChange {
  previous_value: number;
  current_value: number;
  change_percentage: number;
}

interface MetricDistribution {
  contributed_users_count: CounterWithChange;
  total_accumulated_value: CounterWithChange;
}

interface DataInfoMap {
  data_distribution: {
    steps: MetricDistribution;
    distance: MetricDistribution;
    caloriesburned: MetricDistribution;
  };
  total_contributed_users_count: {
    current_value: number;
    previous_value: number;
    change_percentage: number;
  };
}

interface ScoreWithChange {
  current_score: number;
  previous_score: number;
  change_percentage: number;
}

interface OrgPerformanceMap {
  org_health_score:     ScoreWithChange;
  participation_score:  ScoreWithChange;
  overall_performance:  ScoreWithChange;
  consistency:          ScoreWithChange;
}

interface MetricSummary {
  target:         number;
  value:          number;
  unit:           string;
  performance:    number;
  gcr:            number;
  goal_count:     number;
  achieved_goals: number;
  avg_value?:     number;
  max_value?:     number;
  min_value?:     number;
}

interface PeriodSummary {
  type:                  string;
  avg_performance:       number;
  avg_gcr:               number;
  total_achieved_goals:  number;
  total_goal_count:      number;
  steps:                 MetricSummary;
  distance:              MetricSummary;
  caloriesburned:        MetricSummary;
}

interface SummarizedMetricsDataMap {
  current_data:  PeriodSummary[];
  previous_data: PeriodSummary[];
}

interface Performer {
  org_uid:   string;
  user_uid:  string;
  user_name: string;
  image_uid: string | null;
  timestamp: string | null;
  default:   boolean;
  unit:      string;
  value:     number;
}

interface TopPerformers {
  current_performers:  Performer[];
  previous_performers: Performer[];
}

interface DashboardData {
  org_uid: string;
  timeline: {
    current:  DateRange;
    previous: DateRange;
  };
  users_info_map:             UsersInfoMap;
  data_info_map:              DataInfoMap;
  org_performance_map:        OrgPerformanceMap;
  summarized_metrics_data_map: SummarizedMetricsDataMap;
  top_performers:             TopPerformers;
}

interface ApiResponse {
  type:    string;
  status:  string;
  message: string;
  data:    DashboardData;
}

// ─── STYLED-COMPONENT PROP INTERFACES ────────────────────────────────────────
interface PillProps {
  $current?: boolean;
}

interface PillDotProps {
  $current?: boolean;
}

interface GridProps {
  $cols?: string;
  $gap?:  string;
  $mb?:   string;
}

interface CardProps {
  $delay?: string;
  $p?:     string;
}

interface AccentCardProps extends CardProps {
  $accent?: string;
}

interface StatIconBoxProps {
  $bg?: string;
}

interface BadgeProps {
  $up?:   boolean;
  $down?: boolean;
}

interface BarTrackProps {
  $h?: string;
  $m?: string;
}

interface BarFillProps {
  $pct?:   number;
  $color?: string;
}

interface MetricDotProps {
  $c: string;
}

interface RankBadgeProps {
  $r: number;
}

interface AvatarProps {
  $bg: string;
}

interface ScoreBarFillProps {
  $v: number;
}

// ─── KEYFRAMES ───────────────────────────────────────────────────────────────
const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const fadeSlideUp = keyframes`
  from { opacity: 0; transform: translateY(14px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const shimmer = keyframes`
  0%   { background-position: -600px 0; }
  100% { background-position:  600px 0; }
`;

// ─── HELPERS ─────────────────────────────────────────────────────────────────
function fmt(n: number | null | undefined, dec = 0): string {
  if (n == null) return "—";
  if (Math.abs(n) >= 1_000_000) return (n / 1_000_000).toFixed(1) + "M";
  if (Math.abs(n) >= 1_000)     return (n / 1_000).toFixed(1) + "k";
  return Number(n).toFixed(dec);
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
  });
}

function getInitials(name = ""): string {
  return name.split(" ").slice(0, 2).map((w) => w[0] ?? "").join("").toUpperCase() || "?";
}

function avatarBg(name = ""): string {
  const colors = [C.teal, "#3B82F6", "#8B5CF6", "#F59E0B", "#EF4444", "#10B981"];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
  return colors[Math.abs(h) % colors.length];
}

// ─── STYLED COMPONENTS ───────────────────────────────────────────────────────
const PageWrap = styled.div`
  background: ${C.bg};
  min-height: 100%;
  padding: 32px 32px 48px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  overflow-x: clip;
  @media (max-width: 768px) {
    padding: 24px 20px 40px;
  }
  @media (max-width: 560px) {
    padding: 18px 14px 32px;
  }
`;

const HeaderRow = styled.div`
  position: relative;
  z-index: 80;
  display: flex;
  justify-content: space-between;
  align-items: flex-end; /* Aligns bottom of text with bottom of date controls */
  flex-wrap: wrap;
  gap: 20px;
  margin-bottom: 16px;
  animation: ${fadeSlideUp} 0.35s ease both;
  @media (max-width: 560px) {
    gap: 14px;
  }
`;
const HeaderTitles = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  margin-bottom: 4px; /* Slight tweak to align with inputs */
  min-width: 0;
  @media (max-width: 560px) {
    width: 100%;
  }
`;

const PageTitle = styled.h1`
  font-size: 26px; /* Slightly smaller to fit better inline */
  font-weight: 700;
  color: ${C.text};
  line-height: 1.2;
  margin-bottom: 6px;
  @media (max-width: 560px) {
    font-size: 22px;
  }
`;

const PageSub = styled.p`
  font-size: 13px;
  color: ${C.muted};
  margin: 0;
  @media (max-width: 560px) {
    font-size: 12px;
  }
`;
const DateControlsCard = styled.div`
  position: relative;
  z-index: 90;
  @media (max-width: 560px) {
    width: 100%;
  }
`;

const DateTriggerButton = styled.button`
  background: ${C.surface};
  border: 1px solid ${C.border};
  border-radius: 999px;
  padding: 10px 16px;
  box-shadow: ${C.shadow};
  display: flex;
  align-items: center;
  gap: 12px;
  color: ${C.text};
  cursor: pointer;
  transition: border-color 0.18s, transform 0.12s, box-shadow 0.18s;
  &:hover {
    border-color: ${C.teal};
    box-shadow: ${C.shadowMd};
  }
  @media (max-width: 560px) {
    width: 100%;
    justify-content: flex-start;
    padding: 10px 14px;
  }
`;

const DateTriggerIcon = styled.span`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${C.tealLight};
  color: ${C.tealDark};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  flex-shrink: 0;
`;

const DateTriggerText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const DateTriggerLabel = styled.span`
  font-size: 11px;
  font-weight: 700;
  color: ${C.muted};
  text-transform: uppercase;
  letter-spacing: 0.08em;
`;

const DateTriggerValue = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: ${C.text};
  font-family: 'DM Mono', 'Courier New', monospace;
  @media (max-width: 560px) {
    font-size: 12px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const DatePopover = styled.div`
  position: absolute;
  top: calc(100% + 10px);
  right: 0;
  z-index: 9999;
  width: min(420px, calc(100vw - 48px));
  background: ${C.surface};
  border: 1px solid ${C.border};
  border-radius: 18px;
  box-shadow: ${C.shadowMd};
  padding: 16px;
  animation: ${fadeSlideUp} 0.18s ease both;
  @media (max-width: 960px) {
    left: 0;
    right: auto;
    width: min(420px, calc(100vw - 32px));
  }
  @media (max-width: 560px) {
    left: 0px;
    right: 10px;
    transform: none;
    margin-left:-5px;
    width: 273px;
    padding: 14px;
  }
`;

const DatePopoverHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 14px;
`;

const DatePopoverTitle = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: ${C.text};
`;

const DatePopoverSub = styled.div`
  font-size: 11px;
  color: ${C.muted};
`;

const DatePopoverBody = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 12px;
  flex-wrap: wrap;
  @media (max-width: 560px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const DateGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  @media (max-width: 560px) {
    width: 100%;
  }
`;

const DateLabel = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: ${C.muted};
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

const DateInput = styled.input`
  font-family: inherit;
  font-size: 13px;
  font-weight: 500;
  color: ${C.text};
  background: ${C.surfaceAlt};
  border: 1.5px solid ${C.border};
  border-radius: 8px; /* Slimmer border radius */
  padding: 8px 12px; /* Reduced vertical padding */
  outline: none;
  cursor: pointer;
  transition: border-color 0.18s;
  min-width: 140px;
  &:focus { border-color: ${C.teal}; }
  @media (max-width: 560px) {
    min-width: 0;
  }
`;

const FetchBtn = styled.button`
  background: ${C.tealDark};
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 0 20px; 
  height: 36px; /* Matches the new DateInput height */
  font-family: inherit;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: background 0.18s, transform 0.1s, opacity 0.18s;
  white-space: nowrap;
  &:hover:not(:disabled) { background: ${C.teal}; }
  &:active:not(:disabled) { transform: scale(0.97); }
  &:disabled { opacity: 0.55; cursor: not-allowed; }
  @media (max-width: 560px) {
    width: 100%;
  }
`;

// Tweak the PillsRow margin slightly to tighten up the gap
const PillsRow = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 20px; 
  @media (max-width: 560px) {
    gap: 8px;
  }
`;

const Pill = styled.div<PillProps>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 14px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  font-family: 'DM Mono', 'Courier New', monospace;
  ${({ $current }) =>
    $current
      ? css`background: ${C.tealLight}; color: ${C.tealDark}; border: 1.5px solid ${C.teal};`
      : css`background: ${C.surfaceAlt}; color: ${C.textMid}; border: 1.5px solid ${C.border}; box-shadow: inset 0 0 0 1px rgba(255,255,255,0.04);`}
  @media (max-width: 560px) {
    width: 100%;
    padding: 8px 12px;
    font-size: 11px;
    overflow: hidden;
  }
`;

const PillDot = styled.span<PillDotProps>`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${({ $current }) => ($current ? C.teal : C.neutral)};
  flex-shrink: 0;
`;

const SectionLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: ${C.muted};
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid ${C.border};
`;

const Grid = styled.div<GridProps>`
  display: grid;
  grid-template-columns: ${({ $cols }) => $cols ?? "repeat(4, 1fr)"};
  gap: ${({ $gap }) => $gap ?? "14px"};
  margin-bottom: ${({ $mb }) => $mb ?? "24px"};
  @media (max-width: 1100px) {
    grid-template-columns: ${({ $cols }) =>
      $cols?.includes("4") ? "repeat(2, 1fr)" : ($cols ?? "1fr")};
  }
  @media (max-width: 680px) {
    grid-template-columns: 1fr !important;
  }
`;

const TwoCol = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  margin-bottom: 24px;
  @media (max-width: 860px) { grid-template-columns: 1fr; }
`;

const Card = styled.div<CardProps>`
  background: ${C.surface};
  border-radius: 16px;
  box-shadow: ${C.shadow};
  padding: ${({ $p }) => $p ?? "20px 22px"};
  position: relative;
  overflow: hidden;
  animation: ${fadeSlideUp} 0.38s ${({ $delay }) => $delay ?? "0s"} ease both;
  transition: transform 0.15s, box-shadow 0.15s;
  &:hover { transform: translateY(-2px); box-shadow: ${C.shadowMd}; }
`;

const AccentCard = styled(Card)<AccentCardProps>`
  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 3px;
    background: ${({ $accent }) => $accent ?? C.teal};
    border-radius: 16px 16px 0 0;
  }
`;

const StatIconBox = styled.div<StatIconBoxProps>`
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: ${({ $bg }) => $bg ?? C.tealLight};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 17px;
  margin-bottom: 12px;
`;

const StatLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${C.muted};
  margin-bottom: 4px;
`;

const StatValue = styled.div`
  font-size: 30px;
  font-weight: 700;
  color: ${C.text};
  line-height: 1;
  font-family: 'DM Mono', 'Courier New', monospace;
  margin-bottom: 8px;
`;

const StatMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const StatSub = styled.span`
  font-size: 11px;
  color: ${C.muted};
`;

const Badge = styled.span<BadgeProps>`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 2px 8px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 700;
  font-family: 'DM Mono', 'Courier New', monospace;
  white-space: nowrap;
  ${({ $up, $down }) =>
    $up
      ? css`background: ${C.positiveLight}; color: ${C.tealDark}; border: 1px solid rgba(43,191,170,0.28); box-shadow: inset 0 0 0 1px rgba(43,191,170,0.08);`
      : $down
      ? css`background: ${C.negativeLight}; color: ${C.negative};`
      : css`background: ${C.neutralLight}; color: ${C.neutral};`}
`;

const BarTrack = styled.div<BarTrackProps>`
  background: ${C.bg};
  border-radius: 6px;
  height: ${({ $h }) => $h ?? "6px"};
  overflow: hidden;
  margin: ${({ $m }) => $m ?? "10px 0 6px"};
`;

const BarFill = styled.div<BarFillProps>`
  height: 100%;
  border-radius: 6px;
  background: ${({ $color }) => $color ?? C.teal};
  width: ${({ $pct }) => Math.min($pct ?? 0, 100)}%;
  transition: width 1s cubic-bezier(0.4, 0, 0.2, 1);
`;

const TableWrap = styled.div`
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
`;

const Table = styled.table`
  width: 100%;
  min-width: 760px;
  border-collapse: collapse;
  font-size: 13px;
`;

const Th = styled.th`
  text-align: left;
  padding: 8px 12px;
  font-size: 11px;
  font-weight: 700;
  color: ${C.muted};
  text-transform: uppercase;
  letter-spacing: 0.06em;
  border-bottom: 1.5px solid ${C.border};
  white-space: nowrap;
`;

const Td = styled.td`
  padding: 11px 12px;
  border-bottom: 1px solid ${C.border};
  vertical-align: middle;
  color: ${C.textMid};
`;

const Tr = styled.tr`
  transition: background 0.14s;
  &:last-child td { border-bottom: none; }
  &:hover td { background: ${C.tealLight}; }
`;

const MetricDot = styled.span<MetricDotProps>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ $c }) => $c};
  display: inline-block;
  margin-right: 8px;
  flex-shrink: 0;
`;

const Mono = styled.span`
  font-family: 'DM Mono', 'Courier New', monospace;
  color: ${C.text};
  font-weight: 500;
`;

const PanelCard = styled(Card)``;

const PanelTitle = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: ${C.text};
  margin-bottom: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const GoalRow = styled.div`
  padding: 12px 0;
  border-bottom: 1px solid ${C.border};
  &:last-child { border-bottom: none; }
`;

const MetricSumHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  color:var(--text-color);
`;

const MetricTypeTag = styled.span`
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  background: ${C.tealLight};
  color: ${C.tealDark};
  padding: 2px 9px;
  border-radius: 20px;
`;

const MetricGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-top: 10px;
`;

const MiniStat = styled.div``;

const MiniStatLabel = styled.div`
  font-size: 10px;
  color: ${C.muted};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 2px;
`;

const MiniStatVal = styled.div`
  font-size: 15px;
  font-weight: 700;
  font-family: 'DM Mono', 'Courier New', monospace;
  color: ${C.text};
`;

const PerformerRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 9px 8px;
  border-radius: 10px;
  transition: background 0.14s;
  &:hover { background: ${C.tealLight}; }
`;

const RankBadge = styled.div<RankBadgeProps>`
  width: 26px;
  height: 26px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 800;
  color: #fff;
  flex-shrink: 0;
  background: ${({ $r }) =>
    $r === 1 ? "#F59E0B" : $r === 2 ? "#94A3B8" : $r === 3 ? "#CD7F32" : "#64748B"};
`;

const Avatar = styled.div<AvatarProps>`
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: ${({ $bg }) => $bg};
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  flex-shrink: 0;
  overflow: hidden; /* Added to clip images perfectly to the circle */
`;

const PerformerMeta = styled.div`
  flex: 1;
  min-width: 0;
`;

const PerformerName = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${C.text};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const PerformerEmail = styled.div`
  font-size: 11px;
  color: ${C.muted};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const PerformerScore = styled.div`
  font-family: 'DM Mono', 'Courier New', monospace;
  font-size: 14px;
  font-weight: 700;
  color: ${C.tealDark};
  white-space: nowrap;
`;

const ScoreBar = styled.div`
  width: 56px;
  height: 4px;
  background: ${C.bg};
  border-radius: 2px;
  overflow: hidden;
  margin-top: 3px;
`;

const ScoreBarFill = styled.div<ScoreBarFillProps>`
  height: 100%;
  border-radius: 2px;
  background: ${({ $v }) => ($v > 70 ? C.teal : $v > 40 ? C.calColor : C.negative)};
  width: ${({ $v }) => $v}%;
  transition: width 1s ease;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 36px 20px;
  color: ${C.muted};
  gap: 8px;
`;

const Spinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: #fff;
  border-radius: 50%;
  animation: ${spin} 0.7s linear infinite;
  flex-shrink: 0;
`;

const SkeletonCard = styled.div`
  background: ${C.surface};
  border-radius: 16px;
  height: 120px;
  box-shadow: ${C.shadow};
  background: linear-gradient(90deg, ${C.surface} 25%, #EFF2F4 50%, ${C.surface} 75%);
  background-size: 600px 100%;
  animation: ${shimmer} 1.4s infinite;
`;

const ErrorBanner = styled.div`
  background: ${C.negativeLight};
  border: 1.5px solid ${C.negative};
  border-radius: 12px;
  padding: 14px 18px;
  font-size: 13px;
  color: ${C.negative};
  font-weight: 500;
  margin-bottom: 20px;
`;

const InlineEmpty = styled.div`
  background: ${C.surface};
  border-radius: 16px;
  box-shadow: ${C.shadow};
  padding: 48px 20px;
  text-align: center;
  font-size: 14px;
  color: ${C.muted};
`;

// ─── METRIC CONFIG ────────────────────────────────────────────────────────────
interface MetricConfig {
  key:        keyof Pick<PeriodSummary, "steps" | "distance" | "caloriesburned">;
  label:      string;
  icon:       React.ReactNode;
  color:      string;
  colorLight: string;
  unit:       string;
}
const METRICS: MetricConfig[] = [
  { key: "steps",          label: "Steps",    icon: <IoFootsteps />,       color: C.teal,      colorLight: C.tealLight, unit: ""     },
  { key: "distance",       label: "Distance", icon: <RiPinDistanceFill />, color: C.distColor, colorLight: C.distLight, unit: "m"    },
  { key: "caloriesburned", label: "Calories", icon: <RiFireLine />,        color: C.calColor,  colorLight: C.calLight,  unit: "kcal" },
];

// ─── SUB-COMPONENTS ──────────────────────────────────────────────────────────

interface ChangeBadgeProps {
  pct: number | null | undefined;
}

const ChangeBadge: React.FC<ChangeBadgeProps> = ({ pct }) => {
  if (pct == null || pct === 0) return <Badge>— 0.00%</Badge>;
  if (pct > 0) return <Badge $up>▲ {Math.abs(pct).toFixed(2)}%</Badge>;
  return <Badge $down>▼ {Math.abs(pct).toFixed(2)}%</Badge>;
};

interface PerfCardProps {
  label: string;
  icon:  React.ReactNode;
  data:  ScoreWithChange;
  delay: string;
}

const PerfCard: React.FC<PerfCardProps> = ({ label, icon, data, delay }) => {
  const score    = data?.current_score ?? 0;
  const barColor = score >= 60 ? C.teal : score >= 30 ? C.calColor : C.negative;
  return (
    <Card $delay={delay}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
        <div>
          <StatLabel style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ display: "inline-flex", alignItems: "center", color: C.tealDark }}>
              {icon}
            </span>
            {label}
          </StatLabel>
          <div style={{ fontSize: 30, fontWeight: 700, fontFamily: "'DM Mono','Courier New',monospace", color: C.text, lineHeight: 1 }}>
            {fmt(score, 2)}
            <span style={{ fontSize: 13, color: C.muted, fontWeight: 400 }}> /100</span>
          </div>
        </div>
        <ChangeBadge pct={data?.change_percentage} />
      </div>
      <BarTrack>
        <BarFill $pct={score} $color={barColor} />
      </BarTrack>
      <div style={{ fontSize: 11, color: C.muted }}>
        Previous: {fmt(data?.previous_score, 2)}
      </div>
    </Card>
  );
};

interface PerformerListProps {
  performers: Performer[];
  imagesMap: Record<string, { image: string; timestamp: string }>;
}

const PerformerList: React.FC<PerformerListProps> = ({ performers, imagesMap }) => {
  if (!performers || !performers.length) {
    return (
      <EmptyState>
        <div style={{ fontSize: 30, opacity: 0.3 }}>🏅</div>
        <div>No performer data for this period</div>
      </EmptyState>
    );
  }
  return (
    <>
      {performers.map((p, i) => {
        // Find if we have fetched image data for this user
        const imgData = p.image_uid ? imagesMap[p.image_uid] : null;
        const hasValidImage = imgData && imgData.image && imgData.image.length > 0;
        
        // Ensure standard base64 string handling
        const imgSrc = hasValidImage 
          ? (imgData.image.startsWith("data:") ? imgData.image : `data:image/jpeg;base64,${imgData.image}`)
          : null;

        return (
          <PerformerRow key={p.user_uid}>
            <RankBadge $r={i + 1}>{i + 1}</RankBadge>
            <Avatar $bg={avatarBg(p.user_name)}>
              {imgSrc ? (
                <img 
                  src={imgSrc} 
                  alt={p.user_name} 
                  style={{ width: "100%", height: "100%", objectFit: "cover" }} 
                />
              ) : (
                getInitials(p.user_name)
              )}
            </Avatar>
            <PerformerMeta>
              <PerformerName>{p.user_name}</PerformerName>
              <PerformerEmail>{p.user_uid}</PerformerEmail>
            </PerformerMeta>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
              <PerformerScore>{fmt(p.value, 1)}{p.unit}</PerformerScore>
              <ScoreBar><ScoreBarFill $v={p.value} /></ScoreBar>
            </div>
          </PerformerRow>
        );
      })}
    </>
  );
};

// ─── MAIN COMPONENT ──────────────────────────────────────────────────────────
const OrgDashboard: React.FC = () => {
  useCheckSessionHeartBeat();

  const userUid = localStorage.getItem("user_uid") || "";
  const defaultRange = getDefaultRange();
  const datePopoverRef = useRef<HTMLDivElement | null>(null);

  const [fromDate, setFromDate]     = useState<string>(defaultRange.fromDate);
  const [toDate, setToDate]         = useState<string>(defaultRange.toDate);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState<boolean>(false);
  
  const [dashData, setDashData]     = useState<DashboardData | null>(null);
  const [performerImages, setPerformerImages] = useState<Record<string, { image: string; timestamp: string }>>({});
  const [loading, setLoading]       = useState<boolean>(false);
  const [error, setError]           = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);

  const fetchDashboard = useCallback(async (): Promise<void> => {
    if (!fromDate || !toDate) return;

    setLoading(true);
    setError(null);
    setDashData(null);
    setPerformerImages({}); // Reset images on new fetch

    try {
      const response = await axios.get(`${beAddress}/api/get-admin-dashboard`, {
        headers: {
          "user-uid": userUid,
        },
        withCredentials: true,
        params: {
          from_date: toApiDateTime(fromDate, "00:00:00"),
          to_date: toApiDateTime(toDate, "23:59:59"),
        },
      });

      const responseStatus = response.data?.status;
      const responseMessage =
        response.data?.data || "Failed to get admin dashboard data";

      if (responseStatus !== "Success" && responseStatus !== "success") {
        setDashData(null);
        setError(responseMessage);
        openNotification({
          type: "error",
          message: responseMessage,
        });
        return;
      }

      setDashData(response.data.data);
      setLastUpdated(new Date().toLocaleTimeString("en-IN"));
    } catch (fetchError: any) {
      console.error("Error fetching org dashboard:", fetchError);
      const message =
        fetchError?.response?.data ||
        fetchError?.message ||
        "Failed to fetch org dashboard response.";
      setDashData(null);
      setError(message);
      openNotification({
        type: "error",
        message,
      });
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, userUid]);

  const fetchImagesByUid = async (
    imageUids: string[],
  ): Promise<Record<string, { image: string; timestamp: string }>> => {
    if (!imageUids || imageUids.length === 0) {
      return {};
    }

    try {
      const url = new URL(`${beAddress}/api/get-image-by-uid`);
      imageUids.forEach((uid) =>
        url.searchParams.append("image_uid_list", uid),
      );
      url.searchParams.set("is_icon_img", "false");
      url.searchParams.set("is_feedback_image", "false");

      const res = await fetch(url.toString(), {
        headers: {
          "user-uid": userUid,
        },
        credentials: "include",
      });
      const data = await res.json();

      if (data.status === "Success" && Array.isArray(data.data)) {
        return Object.fromEntries(
          data.data.map((item: any) => {
            if (item.default)
              return [item.image_uid, { image: "", timestamp: "" }];
            return [
              item.image_uid,
              { image: item.image, timestamp: item.timstamp },
            ];
          }),
        );
      }
    } catch (err) {
      console.error("Failed to fetch images:", err);
    }

    return {};
  };

  // Initial fetch on mount
  useEffect(() => {
    fetchDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch images specifically for the top performers whenever dashData updates
  useEffect(() => {
    if (dashData?.top_performers?.current_performers) {
      // Extract valid image_uids from the current performers list
      const uidsToFetch = dashData.top_performers.current_performers
        .map(p => p.image_uid)
        .filter((uid): uid is string => Boolean(uid)); // Only keep valid strings

      if (uidsToFetch.length > 0) {
        fetchImagesByUid(uidsToFetch).then(imagesMap => {
          setPerformerImages(imagesMap);
        });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashData]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        datePopoverRef.current &&
        !datePopoverRef.current.contains(event.target as Node)
      ) {
        setIsDatePickerOpen(false);
      }
    };

    if (isDatePickerOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDatePickerOpen]);

  // ── Derived data (safe defaults) ─────────────────────────────────────────
  const d    = dashData;
  const ui   = d?.users_info_map;
  const pm   = d?.org_performance_map;
  const di   = d?.data_info_map;
  const sm   = d?.summarized_metrics_data_map;
  const tp   = d?.top_performers;
  const tl   = d?.timeline;
  
  const curr = sm?.current_data?.[0]  ?? ({} as Partial<PeriodSummary>);
  const prev = sm?.previous_data?.[0] ?? ({} as Partial<PeriodSummary>);

  const totalContribCurr = di?.total_contributed_users_count?.current_value   ?? 0;
  const totalContribChg  = di?.total_contributed_users_count?.change_percentage ?? 0;

  return (
    <PageWrap>
      {/* ── Compact Header & Date Row ── */}
      <HeaderRow>
        <HeaderTitles>
          <PageTitle>Org Dashboard</PageTitle>
          <PageSub>
            {d
              ? `Showing analytics for ${d.org_uid} · Last updated ${lastUpdated ?? ""}`
              : "Analytics for your organisation"}
          </PageSub>
        </HeaderTitles>

        <DateControlsCard ref={datePopoverRef}>
          <DateTriggerButton
            type="button"
            onClick={() => setIsDatePickerOpen((prev) => !prev)}
          >
            <DateTriggerIcon>
              <FaRegCalendarAlt />
            </DateTriggerIcon>
            <DateTriggerText>
              <DateTriggerLabel>Date Range</DateTriggerLabel>
              <DateTriggerValue>
                {fmtDate(fromDate)} {"->"} {fmtDate(toDate)}
              </DateTriggerValue>
            </DateTriggerText>
          </DateTriggerButton>

          {isDatePickerOpen && (
            <DatePopover>
              <DatePopoverHeader>
                <div>
                  <DatePopoverTitle>Select Timeline</DatePopoverTitle>
                  <DatePopoverSub>Choose the period for organisation analytics.</DatePopoverSub>
                </div>
              </DatePopoverHeader>
              <DatePopoverBody>
                <DateGroup>
                  <DateLabel htmlFor="from-date">From Date</DateLabel>
                  <DateInput
                    id="from-date"
                    type="date"
                    value={fromDate}
                    max={toDate}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFromDate(e.target.value)}
                  />
                </DateGroup>
                <DateGroup>
                  <DateLabel htmlFor="to-date">To Date</DateLabel>
                  <DateInput
                    id="to-date"
                    type="date"
                    value={toDate}
                    min={fromDate}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setToDate(e.target.value)}
                  />
                </DateGroup>
                <FetchBtn
                  onClick={() => {
                    fetchDashboard();
                    setIsDatePickerOpen(false);
                  }}
                  disabled={loading || !fromDate || !toDate}
                >
                  {loading && <Spinner />}
                  {loading ? "Loading…" : "Apply"}
                </FetchBtn>
              </DatePopoverBody>
            </DatePopover>
          )}
        </DateControlsCard>
      </HeaderRow>

      {/* ── Skeleton (first load) ── */}
      {loading && (
        <Grid $cols="repeat(4, 1fr)">
          {Array.from({ length: 4 }).map((_, i) => <SkeletonCard key={i} />)}
        </Grid>
      )}

      {/* ── Dashboard Content ── */}
      {!loading && d && ui && pm && di && sm && tp && tl && (
        <>
          {/* Timeline Pills */}
          <PillsRow>
            <Pill $current>
              <PillDot $current />
              Current: {fmtDate(tl.current.from_date)} → {fmtDate(tl.current.to_date)}
            </Pill>
            <Pill>
              <PillDot />
              Previous: {fmtDate(tl.previous.from_date)} → {fmtDate(tl.previous.to_date)}
            </Pill>
          </PillsRow>

          {/* ── USER OVERVIEW ── */}
          <SectionLabel><FaUsers /> User Overview</SectionLabel>
          <Grid $cols="repeat(3, 1fr)">
            <AccentCard $delay="0.04s" $accent={C.teal}>
              <StatIconBox><FaUsers /></StatIconBox>
              <StatLabel>Total Users</StatLabel>
              <StatValue>{fmt(ui.total_users_count)}</StatValue>
              <StatMeta><StatSub>Organisation-wide</StatSub></StatMeta>
            </AccentCard>

            <AccentCard $delay="0.08s" $accent={C.distColor}>
              <StatIconBox $bg={C.distLight}><FaUserCheck /></StatIconBox>
              <StatLabel>Registered</StatLabel>
              <StatValue>{fmt(ui.total_registered_users_count)}</StatValue>
              <StatMeta>
                <StatSub>All-time</StatSub>
                <Badge>
                  {((ui.total_registered_users_count / ui.total_users_count) * 100).toFixed(1)}%
                </Badge>
              </StatMeta>
            </AccentCard>

            <AccentCard
              $delay="0.12s"
              $accent={ui.registered_users_within_duration_count > 0 ? C.teal : C.neutral}
            >
              <StatIconBox $bg={C.tealLight}><HiMiniUserPlus /></StatIconBox>
              <StatLabel>New in Period</StatLabel>
              <StatValue>{fmt(ui.registered_users_within_duration_count)}</StatValue>
              <StatMeta><StatSub>Registered within this timeline</StatSub></StatMeta>
            </AccentCard>
          </Grid>

          {/* ── ORG PERFORMANCE ── */}
          <SectionLabel><FaTrophy /> Organisation Performance</SectionLabel>
          <Grid $cols="repeat(4, 1fr)">
            <PerfCard label="Health Score"  icon={<FaHeartbeat />}        data={pm.org_health_score}    delay="0.05s" />
            <PerfCard label="Participation" icon={<FaRunning />}          data={pm.participation_score} delay="0.09s" />
            <PerfCard label="Overall Perf." icon={<MdOutlineAutoGraph />} data={pm.overall_performance} delay="0.13s" />
            <PerfCard label="Consistency"   icon={<MdLeaderboard />}      data={pm.consistency}         delay="0.17s" />
          </Grid>

          {/* ── ACTIVITY DISTRIBUTION + GOAL COMPLETION ── */}
          <SectionLabel><MdLeaderboard /> Activity Distribution</SectionLabel>
          <TwoCol>
            {/* Metric Breakdown Table */}
            <PanelCard $delay="0.08s">
              <PanelTitle>
                <span>Metric Breakdown</span>
                <span style={{ fontSize: 12, color: C.muted, display: "flex", alignItems: "center", gap: 6 }}>
                  <strong style={{ color: C.text }}>{totalContribCurr}</strong>&nbsp;contributed users
                  <ChangeBadge pct={totalContribChg} />
                </span>
              </PanelTitle>
              <TableWrap>
                <Table>
                  <thead>
                    <tr>
                      <Th>Metric</Th>
                      <Th>Contributed Users</Th>
                      <Th>Current Total</Th>
                      <Th>Prev Total</Th>
                      <Th>Δ Change</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {METRICS.map((m) => {
                      const row = di.data_distribution[m.key];
                      if (!row) return null;
                      return (
                        <Tr key={m.key}>
                          <Td>
                            <div style={{ display: "flex", alignItems: "center" }}>
                              <MetricDot $c={m.color} />
                              <span style={{ fontWeight: 600, color: C.text }}>{m.label}</span>
                            </div>
                          </Td>
                          <Td>
                            <Mono>{row.contributed_users_count?.current_value ?? 0}</Mono>&nbsp;
                            <ChangeBadge pct={row.contributed_users_count?.change_percentage} />
                          </Td>
                          <Td><Mono>{fmt(row.total_accumulated_value?.current_value)}{m.unit}</Mono></Td>
                          <Td style={{ color: C.muted }}>
                            <Mono>{fmt(row.total_accumulated_value?.previous_value)}{m.unit}</Mono>
                          </Td>
                          <Td><ChangeBadge pct={row.total_accumulated_value?.change_percentage} /></Td>
                        </Tr>
                      );
                    })}
                  </tbody>
                </Table>
              </TableWrap>
            </PanelCard>

            {/* Goal Completion */}
            <PanelCard $delay="0.12s">
              <PanelTitle>Goal Completion — Current Period</PanelTitle>
              {METRICS.map((m) => {
                const c = (curr[m.key] ?? {}) as Partial<MetricSummary>;
                const p = (prev[m.key] ?? {}) as Partial<MetricSummary>;
                const gcr     = c.gcr ?? 0;
                const perfChg = (c.performance ?? 0) - (p.performance ?? 0);
                return (
                  <GoalRow key={m.key}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 5 }}>
                      <span style={{ display: "flex", alignItems: "center", fontSize: 13, fontWeight: 600, color: C.text }}>
                        <MetricDot $c={m.color} />
                        {m.label}
                      </span>
                      <span style={{ fontSize: 11, color: C.muted }}>
                        {c.achieved_goals ?? 0}/{c.goal_count ?? 0} goals · GCR {gcr.toFixed(2)}%
                      </span>
                    </div>
                    <BarTrack $h="5px" $m="0 0 5px">
                      <BarFill $pct={gcr} $color={m.color} />
                    </BarTrack>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span style={{ fontSize: 11, color: C.muted }}>
                        Perf: {fmt(c.performance, 2)}%
                      </span>
                      <ChangeBadge pct={perfChg} />
                    </div>
                  </GoalRow>
                );
              })}
            </PanelCard>
          </TwoCol>

          {/* ── METRIC DEEP DIVE ── */}
          <SectionLabel><MdOutlineAutoGraph /> Metric Performance Deep Dive</SectionLabel>
          <Grid $cols="repeat(3, 1fr)" $mb="24px">
            {METRICS.map((m, idx) => {
              const c = (curr[m.key] ?? {}) as Partial<MetricSummary>;
              const p = (prev[m.key] ?? {}) as Partial<MetricSummary>;
              const perfChg =
                (p.performance ?? 0) > 0
                  ? (((c.performance ?? 0) - (p.performance ?? 0)) / (p.performance ?? 1)) * 100
                  : 0;
              return (
                <AccentCard key={m.key} $accent={m.color} $delay={`${0.04 + idx * 0.06}s`}>
                  <MetricSumHeader>
                    <span style={{ fontWeight: 700, fontSize: 14 }}>{m.icon}&nbsp;{m.label}</span>
                    <MetricTypeTag>daily</MetricTypeTag>
                  </MetricSumHeader>

                  <div style={{ fontSize: 30, fontWeight: 700, fontFamily: "'DM Mono','Courier New',monospace", color: C.text, lineHeight: 1 }}>
                    {fmt(c.performance, 2)}
                    <span style={{ fontSize: 13, color: C.muted, fontWeight: 400 }}>%</span>
                  </div>

                  <BarTrack>
                    <BarFill $pct={Math.min(c.performance ?? 0, 100)} $color={m.color} />
                  </BarTrack>

                  <MetricGrid>
                    <MiniStat>
                      <MiniStatLabel>GCR</MiniStatLabel>
                      <MiniStatVal>{fmt(c.gcr, 2)}%</MiniStatVal>
                    </MiniStat>
                    <MiniStat>
                      <MiniStatLabel>Goals</MiniStatLabel>
                      <MiniStatVal>{c.achieved_goals ?? 0}/{c.goal_count ?? 0}</MiniStatVal>
                    </MiniStat>
                    {c.avg_value != null && (
                      <MiniStat>
                        <MiniStatLabel>Avg</MiniStatLabel>
                        <MiniStatVal>{fmt(c.avg_value, 0)}</MiniStatVal>
                      </MiniStat>
                    )}
                    {c.max_value != null && (
                      <MiniStat>
                        <MiniStatLabel>Max</MiniStatLabel>
                        <MiniStatVal>{fmt(c.max_value, 0)}</MiniStatVal>
                      </MiniStat>
                    )}
                  </MetricGrid>

                  <div style={{ display: "flex", justifyContent: "space-between", marginTop: 10 }}>
                    <span style={{ fontSize: 11, color: C.muted }}>
                      Prev perf: {fmt(p.performance, 2)}%
                    </span>
                    <ChangeBadge pct={perfChg} />
                  </div>
                </AccentCard>
              );
            })}
          </Grid>

          {/* ── SUMMARY STATS ── */}
          <Grid $cols="repeat(3, 1fr)" $mb="24px">
            {(
              [
                { label: "Avg Performance", icon: <MdOutlineAutoGraph />, curr: curr.avg_performance,    prev: prev.avg_performance,    unit: "%" },
                { label: "Avg GCR",         icon: <FaBullseye />,         curr: curr.avg_gcr,            prev: prev.avg_gcr,            unit: "%" },
                { label: "Goals Achieved",  icon: <FaCheckCircle />,      curr: curr.total_achieved_goals,prev: prev.total_achieved_goals,unit: "" },
              ] as { label: string; icon: React.ReactNode; curr?: number; prev?: number; unit: string }[]
            ).map((item, idx) => {
              const chg = (item.prev ?? 0) > 0
                ? (((item.curr ?? 0) - (item.prev ?? 0)) / (item.prev ?? 1)) * 100
                : 0;
              return (
                <AccentCard key={item.label} $delay={`${0.04 + idx * 0.06}s`} $accent={C.teal}>
                  <StatIconBox>{item.icon}</StatIconBox>
                  <StatLabel>{item.label}</StatLabel>
                  <StatValue>{fmt(item.curr, item.unit === "%" ? 2 : 0)}{item.unit}</StatValue>
                  <BarTrack $h="4px" $m="8px 0 8px">
                    <BarFill $pct={Math.min(item.curr ?? 0, 100)} $color={C.teal} />
                  </BarTrack>
                  <StatMeta>
                    <StatSub>Prev: {fmt(item.prev, item.unit === "%" ? 2 : 0)}{item.unit}</StatSub>
                    <ChangeBadge pct={chg} />
                  </StatMeta>
                </AccentCard>
              );
            })}
          </Grid>

          {/* ── TOP PERFORMERS (CURRENT ONLY - FULL WIDTH) ── */}
          <SectionLabel><FaTrophy /> Top Performers (Current Period)</SectionLabel>
          <PanelCard $delay="0.06s" style={{ marginBottom: "24px" }}>
            <PanelTitle>Top Performers</PanelTitle>
            <PerformerList 
              performers={tp.current_performers} 
              imagesMap={performerImages} 
            />
          </PanelCard>
        </>
      )}

      {/* ── Empty state (before first fetch) ── */}
      {!d && !loading && !error && (
        <InlineEmpty>
          Select a date range and click <strong>Fetch Response</strong> to load your organisation's analytics.
        </InlineEmpty>
      )}

      {!d && !loading && !!error && (
        <InlineEmpty>
          No data available for the selected date range.
        </InlineEmpty>
      )}
    </PageWrap>
  );
};

export default OrgDashboard;
